import {
  analyticsAddPaymentInfo,
  analyticsAddToCart,
  analyticsBeginCheckout,
  analyticsPurchase,
  analyticsUseFeatures,
  analyticsViewCart,
  analyticsViewError,
  analyticsViewPage,
  NavigationEventProduct,
} from "@storebrand-digital/insurance-analytics";
import {
  alertError,
  fieldInputError,
} from "Store/eventFailed/errorDisplayedSlice";
import { nextStep, prevStep } from "Store/steps/stepsSlice";
import {
  AppPartName,
  ecommerceEventProductsFrom,
  generateStbIdCookie,
  isConfirmCheckoutStep,
  isFirstCheckoutStep,
  mapToBrandModelAndYear,
  partnerNameFromPartnerId,
  pushEcommerceEvent,
  pushNavigationEvents,
  stepName,
} from "Utils/analytics";
import {
  googleAnalyticsCategoryOf,
  googleAnalyticsNameOf,
} from "Utils/analytics/analyticsMappings";
import {
  navigationEventProductsFrom,
  supplementaryInfo,
} from "Utils/analytics/analyticsProductsMapping";
import { call, put, select, takeEvery } from "redux-saga/effects";
import {
  cartFetched,
  clearProductAddedToCart,
  orderComplete,
  pageDisplayed,
  setFeatureFlagUsed,
  setPage,
} from "./analyticsSlice";

import { isAdvisorSelector } from "Store/advisor/advisorSelectors";
import { Cart } from "Store/cart/cartInterfaces";
import { cartSelector } from "Store/cart/cartSagas";
import { errorDisplayedStateToString } from "Store/eventFailed/errorDisplayedInterface";
import { InsuranceProduct } from "Store/interfaces/products/checkout";
import { PartnerState } from "Store/partners/partnerInterfaces";
import { PriceCalculationState } from "Store/priceCalculation/priceCalculationInterfaces";
import { ReduxState } from "Store/rootReducer";
import { StepStatusState } from "Store/steps/stepsInterfaces";
import { userIdentificationSucceeded } from "Store/userIdentification/userIdentificationSlice";
import { isAuthenticated } from "Utils/KeycloakWrapper";
import { funnelName } from "Utils/analytics/analyticsTypes";
import { Cookie } from "Utils/cookies/cookieTypes";
import { insuranceNameFromUrl } from "Utils/navigation/urlUtils";
import { createRequest } from "Utils/requestUtils";
import { isAxiosError } from "axios";
import cookies from "js-cookie";
import { AnalyticsState } from "./analyticsInterfaces";

type AllErrorActionTypes = typeof fieldInputError | typeof alertError;

const stepStatusSelector = (state: ReduxState): StepStatusState =>
  state.stepStatus;
const analyticsSelector = (state: ReduxState): AnalyticsState =>
  state.analytics;
const partnerSelector = (state: ReduxState): PartnerState => state.partner;
const priceCalculationSelector = (state: ReduxState): PriceCalculationState =>
  state.priceCalculation;

const navigationEventProducts = (
  insuranceProducts?: InsuranceProduct[],
): NavigationEventProduct[] | undefined => {
  const productNameInUrl = insuranceNameFromUrl(window.location.href);
  if (productNameInUrl) {
    return [
      {
        name: googleAnalyticsNameOf(productNameInUrl),
        category: googleAnalyticsCategoryOf(productNameInUrl),
      },
    ];
  }
  if (insuranceProducts) {
    return navigationEventProductsFrom(insuranceProducts);
  }
  return undefined;
};

export function* pushStepDisplayed(): any {
  const cart: Cart | null = yield select(cartSelector);
  const stepStatus: StepStatusState = yield select(stepStatusSelector);
  const analytics: AnalyticsState = yield select(analyticsSelector);
  const partner: PartnerState = yield select(partnerSelector);

  if (isConfirmCheckoutStep(stepStatus)) {
    if (cart) {
      pushEcommerceEvent(
        analyticsAddPaymentInfo({
          cartId: cart.cartId,
          products: ecommerceEventProductsFrom(cart.products),
          loginMethod: analytics.loginMethod,
          partnerName: partner.partnerId,
          features: analytics.usedFeatureFlags,
        }),
      );
    }

    yield put(
      setPage({
        name: AppPartName.CONFIRM,
      }),
    );
  }
}

export function* pushPageDisplayed(
  sagaAction: ReturnType<typeof pageDisplayed>,
): any {
  const currentPage = sagaAction.payload.pageName;
  const analytics: AnalyticsState = yield select(analyticsSelector);
  const previousPage = analytics.previousPage?.name;
  const cart: Cart | null = yield select(cartSelector);
  const partner: PartnerState = yield select(partnerSelector);
  const stepStatus: StepStatusState = yield select(stepStatusSelector);
  const priceCalculation: PriceCalculationState = yield select(
    priceCalculationSelector,
  );

  if (isConfirmCheckoutStep(stepStatus) && cart) {
    pushEcommerceEvent(
      analyticsAddPaymentInfo({
        cartId: cart.cartId,
        products: ecommerceEventProductsFrom(cart.products),
        loginMethod: analytics.loginMethod,
        partnerName: partnerNameFromPartnerId(partner.partnerId),
        features: analytics.usedFeatureFlags,
      }),
    );
  }

  if (isFirstCheckoutStep(stepStatus) && cart) {
    pushEcommerceEvent(
      analyticsBeginCheckout({
        cartId: cart.cartId,
        products: ecommerceEventProductsFrom(cart.products),
        loginMethod: analytics.loginMethod,
        partnerName: partner.partnerId,
        features: analytics.usedFeatureFlags,
      }),
    );
  }

  if (
    !(
      currentPage === AppPartName.CONFIRM ||
      currentPage === AppPartName.OFFER ||
      currentPage === AppPartName.OFFER_ACCEPT ||
      currentPage === AppPartName.RECEIPT
    ) &&
    !(currentPage === AppPartName.PRODUCT_REDIRECT && previousPage) &&
    (currentPage !== previousPage || currentPage === AppPartName.CHECKOUT_STEP)
  ) {
    pushNavigationEvents(
      analyticsViewPage({
        funnelName,
        features: analytics.usedFeatureFlags,
        partner: partner.partnerId,
        loginMethod: analytics.loginMethod,
        page:
          currentPage +
          `${
            sagaAction.payload.product
              ? ` ${sagaAction.payload.product.toLocaleLowerCase()}`
              : ""
          }`,
        products: navigationEventProducts(cart?.products),
        extendedProductInformation: supplementaryInfo(
          mapToBrandModelAndYear(priceCalculation.priceCalculationFormStates),
        ),
      }),
    );
  }

  yield put(
    setPage({
      name: currentPage,
    }),
  );
}

export function* pushCartFetched(): any {
  const analytics: AnalyticsState = yield select(analyticsSelector);
  const cart: Cart | null = yield select(cartSelector);
  const partner: PartnerState = yield select(partnerSelector);

  if (
    analytics.page?.name === AppPartName.OFFER ||
    analytics.page?.name === AppPartName.OFFER_ACCEPT
  ) {
    if (
      analytics.productsAddedToCart &&
      analytics.productsAddedToCart.length > 0
    ) {
      if (cart) {
        pushEcommerceEvent(
          analyticsAddToCart({
            cartId: cart.cartId,
            addedProducts: ecommerceEventProductsFrom(
              analytics.productsAddedToCart,
            ),
            loginMethod: analytics.loginMethod,
            partnerName: partner.partnerId,
            features: analytics.usedFeatureFlags,
          }),
        );
      }
      yield put(clearProductAddedToCart());
    }

    if (cart) {
      pushEcommerceEvent(
        analyticsViewCart({
          cartId: cart.cartId,
          products: ecommerceEventProductsFrom(cart.products),
          loginMethod: analytics.loginMethod,
          partnerName: partner.partnerId,
          features: analytics.usedFeatureFlags,
        }),
      );
    }
  }
}

export function* pushOrderComplete(): any {
  const analytics: AnalyticsState = yield select(analyticsSelector);
  const cart: Cart | null = yield select(cartSelector);
  const partner: PartnerState = yield select(partnerSelector);
  const priceCalculation: PriceCalculationState = yield select(
    priceCalculationSelector,
  );

  pushNavigationEvents(
    analyticsViewPage({
      funnelName,
      features: analytics.usedFeatureFlags,
      partner: partner.partnerId,
      loginMethod: analytics.loginMethod,
      page: AppPartName.RECEIPT,
      products: navigationEventProducts(cart?.products),
      extendedProductInformation: supplementaryInfo(
        mapToBrandModelAndYear(priceCalculation.priceCalculationFormStates),
      ),
    }),
  );

  if (cart) {
    pushEcommerceEvent(
      analyticsPurchase({
        cartId: cart.cartId,
        products: ecommerceEventProductsFrom(cart.products),
        loginMethod: analytics.loginMethod,
        partnerName: partner.partnerId,
        features: analytics.usedFeatureFlags,
      }),
    );
  }
}

export function* pushErrorDisplayed(
  sagaAction: ReturnType<AllErrorActionTypes>,
): any {
  const cart: Cart | null = yield select(cartSelector);
  const analytics: AnalyticsState = yield select(analyticsSelector);
  const partner: PartnerState = yield select(partnerSelector);
  const priceCalculation: PriceCalculationState = yield select(
    priceCalculationSelector,
  );

  pushNavigationEvents(
    analyticsViewError({
      funnelName,
      errorMessage: errorDisplayedStateToString(sagaAction.payload),
      features: analytics.usedFeatureFlags,
      partner: partner.partnerId,
      loginMethod: analytics.loginMethod,
      page: analytics.page?.name,
      products: navigationEventProducts(cart?.products),
      extendedProductInformation: supplementaryInfo(
        mapToBrandModelAndYear(priceCalculation.priceCalculationFormStates),
      ),
    }),
  );
}

export function* pushLoginSuccess(): any {
  const cart: Cart | null = yield select(cartSelector);
  const analytics: AnalyticsState = yield select(analyticsSelector);
  const partner: PartnerState = yield select(partnerSelector);
  const priceCalculation: PriceCalculationState = yield select(
    priceCalculationSelector,
  );

  if (analytics.page?.name === AppPartName.PRODUCT_REDIRECT) {
    const page = stepName(1, AppPartName.CHECK_PRICE);

    pushNavigationEvents(
      analyticsViewPage({
        funnelName,
        features: analytics.usedFeatureFlags,
        partner: partner.partnerId,
        loginMethod: analytics.loginMethod,
        page: page,
        products: navigationEventProducts(cart?.products),
        extendedProductInformation: supplementaryInfo(
          mapToBrandModelAndYear(priceCalculation.priceCalculationFormStates),
        ),
      }),
    );

    yield put(
      setPage({
        name: page,
      }),
    );
  }
}

export function* featureFlagUsed(
  sagaAction: ReturnType<typeof setFeatureFlagUsed>,
): any {
  const analytics: AnalyticsState = yield select(analyticsSelector);
  pushEcommerceEvent(
    analyticsUseFeatures({
      features: [sagaAction.payload],
      loginMethod: analytics.loginMethod,
    }),
  );
}

export function* pushStbId(): any {
  const isAdvisor: boolean = yield select(isAdvisorSelector);
  let stbId = cookies.get(Cookie.STB_ID);

  if (!stbId) {
    stbId = generateStbIdCookie();
    const date = new Date();
    date.setFullYear(date.getFullYear() + 2);
    cookies.set(Cookie.STB_ID, stbId, {
      expires: date,
      domain: window.env.COOKIE_DOMAIN,
      path: "/",
    });
  }
  if (
    isAuthenticated() &&
    !isAdvisor &&
    window.env.ENVIRONMENT === "production" &&
    stbId &&
    stbId !== ""
  ) {
    try {
      const url = "/customer/tracking/data";
      yield call(createRequest, {
        method: "post",
        requestBody: { stbId: stbId },
        url,
        open: false,
      });
    } catch (e) {
      if (isAxiosError(e)) {
      }
    }
  }
}

export function* analyticsSaga(): Generator {
  yield takeEvery([alertError], pushErrorDisplayed);
  yield takeEvery([nextStep, prevStep], pushStepDisplayed);
  yield takeEvery([pageDisplayed], pushPageDisplayed);
  yield takeEvery([cartFetched], pushCartFetched);
  yield takeEvery([orderComplete], pushOrderComplete);
  yield takeEvery([userIdentificationSucceeded], pushLoginSuccess);
  yield takeEvery([userIdentificationSucceeded], pushStbId);
  yield takeEvery([setFeatureFlagUsed], featureFlagUsed);
}
